import { Image } from 'antd';
import {
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileWordOutlined,
  FileZipOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import resolveAttachmentUrl from '../../utilities/resolveAttachmentUrl';
import { fileExtension } from '../../utilities/model3dFiles';
import { ORDER_ATTACHMENT_EXTENSIONS, ORDER_ATTACHMENT_MAX_BYTES } from '../../constants/allowedAttachments';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'heic', 'heif', 'tiff', 'tif'];
const VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'avi', 'wmv', 'webm', 'mkv', 'mpeg', 'mpg', '3gp', 'flv'];
/** Formats browsers can't render inline even though they are images. */
const NON_RENDERABLE_IMAGES = ['heic', 'heif', 'tiff', 'tif'];

const attachmentExt = (a) => String(a.extension || fileExtension(a.name)).toLowerCase();
const isImage = (a) => a.type === 'image' || IMAGE_EXTENSIONS.includes(attachmentExt(a));
const isVideo = (a) => a.type === 'video' || VIDEO_EXTENSIONS.includes(attachmentExt(a));
const isThumbnailable = (a) => isImage(a) && !NON_RENDERABLE_IMAGES.includes(attachmentExt(a));

/** Validation result key for a picked file, or null when it can be uploaded. */
export const supportFileProblem = (file) => {
  const mime = String(file.type ?? '');
  const allowed = ORDER_ATTACHMENT_EXTENSIONS.includes(fileExtension(file.name))
    || mime.startsWith('image/')
    || mime.startsWith('video/');
  if (!allowed) return 'invalidType';
  if (file.size > ORDER_ATTACHMENT_MAX_BYTES) return 'tooLarge';
  return null;
};

export const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export const attachmentIcon = (attachment) => {
  const ext = attachmentExt(attachment);
  if (isImage(attachment)) return <FileImageOutlined />;
  if (isVideo(attachment)) return <VideoCameraOutlined />;
  if (ext === 'pdf') return <FilePdfOutlined />;
  if (ext === 'zip') return <FileZipOutlined />;
  if (ext === 'doc' || ext === 'docx') return <FileWordOutlined />;
  if (ext === 'xls' || ext === 'xlsx') return <FileExcelOutlined />;
  return <FileOutlined />;
};

const SupportChatAttachments = ({ attachments }) => {
  if (!attachments?.length) return null;
  const images = attachments.filter(isThumbnailable);
  const videos = attachments.filter((a) => !images.includes(a) && isVideo(a));
  const files = attachments.filter((a) => !images.includes(a) && !videos.includes(a));

  return (
    <div className="support-chat__attachments">
      {images.length ? (
        <Image.PreviewGroup>
          <div className="support-chat__media">
            {images.map((a) => (
              <Image
                key={a.id}
                src={resolveAttachmentUrl(a.path)}
                alt={a.name}
                className="support-chat__media-image"
              />
            ))}
          </div>
        </Image.PreviewGroup>
      ) : null}
      {videos.map((a) => (
        <video
          key={a.id}
          className="support-chat__media-video"
          src={resolveAttachmentUrl(a.path)}
          controls
          preload="metadata"
        />
      ))}
      {files.map((a) => (
        <a
          key={a.id}
          className="support-chat__file"
          href={resolveAttachmentUrl(a.path) ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          title={a.name}
        >
          <span className="support-chat__file-icon">{attachmentIcon(a)}</span>
          <span className="support-chat__file-name">{a.name}</span>
          <span className="support-chat__file-size">{formatBytes(a.size)}</span>
        </a>
      ))}
    </div>
  );
};

export default SupportChatAttachments;
